/* global vis */
import React, { Component } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Paper, Typography, Button, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { connect } from 'react-redux';
import CardPage from '../../../commonModules/layouts/CardPage';
import Currency from '../../../commonModules/Currency';
import { isEmpty } from 'lodash';

// Component wrapper to use hooks
const RFIDGraphWrapper = (props) => {
    const { rfid: rfidFromUrl } = useParams();
    
    return <RFIDGraph rfidFromUrl={rfidFromUrl} {...props} />;
};

class RFIDGraph extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: null,
            graphData: null,
            network: null,
            selectedHistory: null,
            selectedEdge: null,
            rfidInput: props.rfidFromUrl || ''
        };
        this.networkContainerRef = React.createRef();
    }

    componentDidMount() {
        // Load vis.js from CDN first
        this.loadVisJs();
        
        if (this.props.rfidFromUrl) {
            this.loadGraphData(this.props.rfidFromUrl);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // If graphData changed and vis.js is loaded, render the graph
        if (this.state.graphData && 
            this.state.graphData !== prevState.graphData && 
            typeof window.vis !== 'undefined') {
            this.renderGraph(this.state.graphData);
        }
        
        // If vis.js just loaded and we have graphData, render it
        if (prevState.loading && !this.state.loading && 
            this.state.graphData && 
            typeof window.vis !== 'undefined') {
            this.renderGraph(this.state.graphData);
        }
    }

    loadVisJs() {
        if (typeof window.vis !== 'undefined') {
            return; // Already loaded
        }

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/vis-network/standalone/umd/vis-network.min.js';
        script.onload = () => {
            if (this.state.graphData) {
                this.renderGraph(this.state.graphData);
            }
        };
        document.head.appendChild(script);
    }

    async loadGraphData(rfid) {
        this.setState({ loading: true, error: null });

        try {
            const response = await fetch(`/api/rfid/history/graph/${rfid}`, {
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            // Read response once - check content type first
            const contentType = response.headers.get('content-type');
            const isJson = contentType && contentType.includes('application/json');
            
            let data;
            if (isJson) {
                // Read the response body once
                const responseText = await response.text();
                try {
                    data = JSON.parse(responseText);
                } catch (parseError) {
                    console.error('Failed to parse JSON:', parseError, 'Response:', responseText);
                    throw new Error(`Erreur de parsing JSON: ${parseError.message}`);
                }
            } else {
                // Non-JSON response
                const text = await response.text();
                throw new Error(`Réponse inattendue (${contentType || 'unknown'}): ${text.substring(0, 200)}`);
            }

            // Now check if it's an error response
            if (!response.ok) {
                const errorMessage = data.error || `Erreur HTTP ${response.status}`;
                throw new Error(errorMessage);
            }

            console.log('RFID Graph data received:', data);
            
            // Validate response data
            if (!data) {
                throw new Error('Aucune donnée reçue du serveur');
            }
            
            if (!data.nodes || !Array.isArray(data.nodes)) {
                console.error('Invalid graph data structure:', data);
                throw new Error(`Format de données invalide: nodes manquant ou invalide. Reçu: ${JSON.stringify(data)}`);
            }

            this.setState({ graphData: data, loading: false });
            
            // Render graph if vis.js is loaded
            if (typeof window.vis !== 'undefined') {
                this.renderGraph(data);
            } else {
                // Wait a bit for vis.js to load
                const checkVisLoaded = setInterval(() => {
                    if (typeof window.vis !== 'undefined') {
                        clearInterval(checkVisLoaded);
                        this.renderGraph(data);
                    }
                }, 100);
                
                // Timeout after 5 seconds
                setTimeout(() => {
                    clearInterval(checkVisLoaded);
                    if (typeof window.vis === 'undefined') {
                        this.setState({ 
                            error: 'Erreur: vis.js n\'a pas pu être chargé',
                            loading: false 
                        });
                    }
                }, 5000);
            }
        } catch (error) {
            console.error('Error loading graph data:', error);
            this.setState({ 
                error: error.message || 'Erreur lors du chargement des données du graphique',
                loading: false 
            });
        }
    }

    renderGraph(data) {
        if (!this.networkContainerRef.current) {
            return;
        }

        // Validate data structure
        if (!data || typeof data !== 'object') {
            console.error('Invalid graph data:', data);
            this.setState({ 
                error: 'Données du graphique invalides',
                loading: false 
            });
            return;
        }

        const graphNodes = data.nodes || [];
        const graphEdges = data.edges || [];

        // Validate that we have nodes
        if (!Array.isArray(graphNodes) || graphNodes.length === 0) {
            console.error('No nodes found in graph data:', data);
            this.setState({ 
                error: 'Aucun historique trouvé pour ce RFID',
                loading: false 
            });
            return;
        }

        // Check if vis.js is loaded
        if (typeof window.vis === 'undefined') {
            console.error('vis.js is not loaded');
            return;
        }

        // Log nodes for debugging
        console.log('Total history entries:', graphNodes.length);
        console.log('Total connections:', graphEdges.length);
        
        // Store node data mapping for later access
        const nodeDataMap = {};
        graphNodes.forEach(node => {
            nodeDataMap[node.history_id] = node;
        });

        // Store edge data mapping for later access
        const edgeDataMap = {};
        graphEdges.forEach((edge, index) => {
            edgeDataMap[index] = edge;
        });

        // Create nodes for vis.js
        const nodes = new window.vis.DataSet(
            graphNodes.map(node => ({
                id: node.history_id,
                label: `${node.shop_name}\n${node.op_type}\n${node.date}\n${this.formatCurrencyString(node.cost || 0)}\nQté: ${node.quantity}`,
                title: `History: ${node.history_id}\nRFID: ${node.rfid}\nProduit: ${node.product_name}\nShop: ${node.shop_name}\nType: ${node.op_type}\nDate: ${node.date}\nQuantité: ${node.quantity}\nPrix U: ${this.formatCurrencyString(node.price_u || 0)}\nPrix HT: ${this.formatCurrencyString(node.price_ht || 0)}\nPrix TTC: ${this.formatCurrencyString(node.price_ttc || 0)}\nCoût: ${this.formatCurrencyString(node.cost || 0)}\nStatus: ${node.status}`,
                color: this.getNodeColor(node.op_type, node.available),
                shape: 'box',
                font: {
                    size: 12,
                    color: 'white',
                    bold: true
                },
                borderWidth: node.available ? 3 : 1,
                shadow: {
                    enabled: true
                }
            }))
        );

        // Create edges for vis.js
        const edges = new window.vis.DataSet(
            graphEdges.map((edge, index) => {
                let title = `Transfer: ${edge.transfer_id || 'N/A'}\nType: ${edge.op_type || ''}\nDate: ${edge.date || ''}\nQuantité: ${edge.quantity || 0}\nPrix U: ${this.formatCurrencyString(edge.price_u || 0)}\nPrix HT: ${this.formatCurrencyString(edge.price_ht || 0)}\nPrix TTC: ${this.formatCurrencyString(edge.price_ttc || 0)}\nCoût: ${this.formatCurrencyString(edge.cost || 0)}`;
                
                // Ajouter Transport et Taxes si présents
                if (edge.transport && edge.transport > 0) {
                    title += `\nTransport: ${this.formatCurrencyString(edge.transport)}`;
                }
                if (edge.taxes && edge.taxes > 0) {
                    title += `\nTaxes: ${this.formatCurrencyString(edge.taxes)}`;
                }
                
                return {
                    id: index,
                    from: edge.from,
                    to: edge.to,
                    label: edge.label || '',
                    title: title,
                    arrows: 'to',
                    color: { color: '#2196F3' },
                    width: 2,
                    font: {
                        size: 10,
                        color: '#333',
                        background: 'rgba(255, 255, 255, 0.8)',
                        strokeWidth: 3,
                        strokeColor: 'white',
                        align: 'top'
                    },
                    smooth: {
                        type: 'cubicBezier',
                        roundness: 0.2
                    }
                };
            })
        );

        // Network options
        const options = {
            layout: {
                hierarchical: {
                    enabled: true,
                    direction: 'LR', // Left to Right
                    sortMethod: 'directed',
                    levelSeparation: 400,
                    nodeSpacing: 200,
                    treeSpacing: 250,
                    blockShifting: true,
                    edgeMinimization: true,
                    parentCentralization: true
                }
            },
            physics: {
                enabled: false
            },
            edges: {
                font: {
                    size: 10,
                    align: 'top',
                    color: '#333',
                    background: 'rgba(255, 255, 255, 0.8)',
                    strokeWidth: 3,
                    strokeColor: 'white'
                },
                arrows: {
                    to: {
                        enabled: true,
                        scaleFactor: 1.2
                    }
                }
            },
            nodes: {
                widthConstraint: {
                    minimum: 180,
                    maximum: 250
                },
                margin: 15
            },
            interaction: {
                hover: true,
                tooltipDelay: 100,
                dragNodes: true,
                dragView: true,
                zoomView: true
            }
        };

        // Create network
        const container = this.networkContainerRef.current;
        const networkData = { nodes, edges };
        const network = new window.vis.Network(container, networkData, options);

        // Handle node and edge clicks
        network.on('click', (params) => {
            if (params.nodes.length > 0) {
                // Click on a node
                const nodeId = params.nodes[0];
                const node = nodeDataMap[nodeId];
                if (node) {
                    this.setState({ selectedHistory: node, selectedEdge: null });
                }
            } else if (params.edges.length > 0) {
                // Click on an edge
                const edgeId = params.edges[0];
                const edge = edgeDataMap[edgeId];
                if (edge) {
                    this.setState({ selectedEdge: edge, selectedHistory: null });
                }
            } else {
                // Click on empty space - clear selection
                this.setState({ selectedHistory: null, selectedEdge: null });
            }
        });

        // Center view after rendering
        network.once('afterDrawing', () => {
            network.fit({
                animation: {
                    duration: 500,
                    easingFunction: 'easeInOutQuad'
                }
            });
        });

        this.setState({ network });
    }

    formatCurrencyString(value) {
        const currency = this.props.currency || 'Euro';
        const numValue = parseFloat(value) || 0;
        
        let formattedValue;
        let symbol;
        
        if (currency === 'Euro') {
            formattedValue = (Math.round(numValue * 100) / 100).toFixed(2);
            symbol = '€';
        } else {
            formattedValue = Math.round(numValue).toLocaleString('fr-FR');
            symbol = 'FCFA';
        }
        
        return `${formattedValue} ${symbol}`;
    }

    getNodeColor(opType, available) {
        // Color code by operation type
        const colors = {
            'reapprovisionnement': '#4CAF50',
            'reservation': '#2196F3',
            'installation': '#FF9800',
            'vente': '#E91E63',
            'payg': '#9C27B0',
            'achat': '#00BCD4',
            'commercial': '#795548',
            'reapprovisionnement_ext': '#607D8B',
            'arrivage': '#009688',
            'demande': '#FFC107',
            'sav': '#F44336'
        };
        
        let color = colors[opType] || '#757575';
        
        // Darker shade if not available
        if (!available) {
            color = color + '80'; // Add alpha for transparency
        }
        
        return color;
    }

    changeLayout(direction) {
        if (this.state.network) {
            const layoutOptions = {
                layout: {
                    hierarchical: {
                        enabled: true,
                        direction: direction,
                        sortMethod: 'directed',
                        levelSeparation: direction === 'UD' || direction === 'DU' ? 300 : 400,
                        nodeSpacing: direction === 'UD' || direction === 'DU' ? 200 : 200,
                        treeSpacing: 250,
                        blockShifting: true,
                        edgeMinimization: true,
                        parentCentralization: true
                    }
                }
            };
            
            this.state.network.setOptions(layoutOptions);
            
            setTimeout(() => {
                this.state.network.fit({
                    animation: {
                        duration: 500,
                        easingFunction: 'easeInOutQuad'
                    }
                });
            }, 100);
        }
    }

    handleRFIDInputChange = (event) => {
        this.setState({ rfidInput: event.target.value });
    }

    handleRFIDSubmit = (event) => {
        event.preventDefault();
        const rfid = this.state.rfidInput.trim();
        if (rfid) {
            this.loadGraphData(rfid);
        }
    }

    render() {
        const { loading, error, graphData, selectedHistory, selectedEdge, rfidInput } = this.state;

        return (
            <CardPage>
                <Box p={2}>
                    <Typography variant="h4" gutterBottom>
                        Graphique de l'Historique RFID
                    </Typography>

                    {/* RFID Input Form */}
                    <Box mb={2}>
                        <form onSubmit={this.handleRFIDSubmit} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <TextField
                                label="Entrer un RFID"
                                value={rfidInput}
                                onChange={this.handleRFIDInputChange}
                                variant="outlined"
                                size="small"
                                placeholder="RFID..."
                                style={{ flex: 1, maxWidth: '400px' }}
                            />
                            <Button 
                                type="submit" 
                                variant="contained" 
                                color="primary"
                                disabled={!rfidInput.trim() || loading}
                            >
                                Charger
                            </Button>
                        </form>
                    </Box>

                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                            <Typography variant="body1" style={{ marginLeft: 16 }}>
                                Chargement du graphique...
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" style={{ marginTop: 16 }}>
                            {error}
                        </Alert>
                    )}

                    {graphData && !loading && (
                        <>
                            <Box mb={2}>
                                <Typography variant="body2" color="textSecondary">
                                    RFID: <strong>{graphData.rfid}</strong> | 
                                    {graphData.nodes ? graphData.nodes.length : 0} mouvement(s) dans l'historique
                                    {graphData.edges && `, ${graphData.edges.length} lien(s)`}
                                </Typography>
                            </Box>

                            <Box mb={2}>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('LR')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Horizontale (G → D)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('UD')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Verticale (H → B)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => this.changeLayout('RL')}
                                    style={{ marginRight: 8 }}
                                >
                                    Vue Horizontale (D → G)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    onClick={() => {
                                        if (this.state.network) {
                                            this.state.network.fit();
                                        }
                                    }}
                                >
                                    Recentrer
                                </Button>
                            </Box>

                            <Paper style={{ padding: 16, marginBottom: 16 }}>
                                <div 
                                    ref={this.networkContainerRef}
                                    style={{ 
                                        width: '100%', 
                                        height: '600px', 
                                        border: '1px solid #ddd',
                                        borderRadius: 4
                                    }}
                                />
                            </Paper>

                            {selectedHistory && (
                                <Paper style={{ padding: 16, marginTop: 16 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Détails du Mouvement (Nœud)
                                    </Typography>
                                    <Box style={{ userSelect: 'text', cursor: 'text' }}>
                                        <Typography variant="body2">
                                            <strong>History ID:</strong> {selectedHistory.history_id}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>RFID:</strong> {selectedHistory.rfid}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Transfer ID:</strong> {selectedHistory.transfer_id}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Type d'opération:</strong> {selectedHistory.op_type}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Date:</strong> {selectedHistory.date}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Produit:</strong> {selectedHistory.product_name} ({selectedHistory.product_id})
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Shop:</strong> {selectedHistory.shop_name} ({selectedHistory.shop_id})
                                        </Typography>
                                        {selectedHistory.from_shop && (
                                            <Typography variant="body2">
                                                <strong>Provenant de:</strong> {selectedHistory.from_shop_name} ({selectedHistory.from_shop})
                                            </Typography>
                                        )}
                                        <Typography variant="body2">
                                            <strong>Quantité:</strong> {selectedHistory.quantity}
                                        </Typography>
                                        <Box mt={1} mb={1} style={{ borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                                            <Typography variant="body2" color="primary">
                                                <strong>Détails Prix:</strong>
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix Unitaire:</strong> <Currency value={selectedHistory.price_u || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix HT:</strong> <Currency value={selectedHistory.price_ht || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Prix TTC:</strong> <Currency value={selectedHistory.price_ttc || 0} />
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Coût:</strong> <Currency value={selectedHistory.cost || 0} />
                                            </Typography>
                                            {selectedHistory.surcout > 0 && selectedHistory.surcout !== 1 && (
                                                <Typography variant="body2">
                                                    <strong>Surcout:</strong> {selectedHistory.surcout.toFixed(2)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="body2">
                                            <strong>Status:</strong> {selectedHistory.status}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Disponible:</strong> {selectedHistory.available ? '✓ Oui' : '✗ Non'}
                                        </Typography>
                                    </Box>
                                </Paper>
                            )}

                            {selectedEdge && (
                                <Paper style={{ padding: 16, marginTop: 16 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Détails du Transfer (Flèche)
                                    </Typography>
                                    <Box style={{ userSelect: 'text', cursor: 'text' }}>
                                        <Typography variant="body2">
                                            <strong>Transfer ID:</strong> {selectedEdge.transfer_id}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Type:</strong> {selectedEdge.op_type}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Date:</strong> {selectedEdge.date}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Quantité:</strong> {selectedEdge.quantity}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Prix U:</strong> <Currency value={selectedEdge.price_u || 0} />
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Prix HT:</strong> <Currency value={selectedEdge.price_ht || 0} />
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Prix TTC:</strong> <Currency value={selectedEdge.price_ttc || 0} />
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Coût:</strong> <Currency value={selectedEdge.cost || 0} />
                                        </Typography>
                                        {selectedEdge.transport > 0 && (
                                            <Typography variant="body2">
                                                <strong>Transport:</strong> <Currency value={selectedEdge.transport} />
                                            </Typography>
                                        )}
                                        {selectedEdge.taxes > 0 && (
                                            <Typography variant="body2">
                                                <strong>Taxes:</strong> <Currency value={selectedEdge.taxes} />
                                            </Typography>
                                        )}
                                    </Box>
                                </Paper>
                            )}
                        </>
                    )}

                    {!graphData && !loading && !error && (
                        <Alert severity="info">
                            Entrez un RFID ci-dessus pour afficher son historique de mouvements.
                        </Alert>
                    )}
                </Box>
            </CardPage>
        );
    }
}

// Map Redux state to props
function mapStateToProps(state) {
    return {
        currency: state.currency?.currency || 'Euro'
    };
}

// Connect RFIDGraph to Redux for currency
const ConnectedRFIDGraph = connect(mapStateToProps)(RFIDGraph);

// Update wrapper to use connected component
const RFIDGraphWrapperConnected = (props) => {
    const { rfid: rfidFromUrl } = useParams();
    
    return <ConnectedRFIDGraph rfidFromUrl={rfidFromUrl} {...props} />;
};

export default RFIDGraphWrapperConnected;

